import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShoppingCart } from "lucide-react";
import { LOGO_WHITE_URL } from "../config/brand";
import { useCart } from "../context/CartContext";
import { Ticker } from "./Ticker";
import { MenuOverlay } from "./MenuOverlay";
import { SearchOverlay } from "./SearchOverlay";
import { CartDrawer } from "./CartDrawer";

const Hamburger = () => (
  <span className="flex flex-col justify-between w-6 h-[14px] md:w-7 md:h-[16px]" aria-hidden="true">
    <span className="block h-[1.5px] w-full bg-white" />
    <span className="block h-[1.5px] w-full bg-white" />
    <span className="block h-[1.5px] w-full bg-white" />
  </span>
);

/*
 * Paly-style chrome (desktop + mobile):
 *  - Ticker pinned at the very top (sticky, in flow).
 *  - Header row (logo left, hamburger / search / cart right) is FIXED just under the ticker
 *    with no background; it floats over the hero and stays while scrolling.
 *    mix-blend-difference keeps the white logo/icons readable over white or dark content.
 */
export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tickerH, setTickerH] = useState(0);
  const tickerRef = useRef(null);
  const { count, openCart } = useCart();

  useEffect(() => {
    const el = tickerRef.current;
    if (!el) return;
    const measure = () => setTickerH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const searchFromMenu = (q) => {
    setMenuOpen(false);
    setSearchQuery(q);
    setSearchOpen(true);
  };

  return (
    <>
      <div ref={tickerRef} className="sticky top-0 z-40" data-testid="ticker-bar">
        <Ticker />
      </div>

      <header
        className="fixed inset-x-0 z-40 flex items-center justify-between h-12 md:h-16 px-4 md:px-6 pointer-events-none mix-blend-difference text-white"
        style={{ top: tickerH }}
        data-testid="site-header"
      >
        <Link
          to="/"
          data-testid="header-logo-link"
          className="pointer-events-auto flex-shrink min-w-0"
          aria-label="Rich Kids Funeral — home"
        >
          <img
            src={LOGO_WHITE_URL}
            alt="Rich Kids Funeral"
            className="h-7 sm:h-8 md:h-12 w-auto max-w-[56vw] object-contain"
            data-testid="header-logo"
          />
        </Link>
        <div className="pointer-events-auto flex items-center gap-5 md:gap-7 flex-shrink-0">
          <button
            data-testid="menu-open-button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="hover:opacity-50 transition-opacity"
          >
            <Hamburger />
          </button>
          <button
            data-testid="search-open-button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            className="hover:opacity-50 transition-opacity"
          >
            <Search className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />
          </button>
          <button
            data-testid="cart-open-button"
            onClick={openCart}
            aria-label={`Cart, ${count} items`}
            className="flex items-center gap-1.5 hover:opacity-50 transition-opacity"
          >
            <ShoppingCart className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />
            <span className="text-[14px] md:text-[15px] font-semibold tabular-nums" data-testid="cart-count">
              {count}
            </span>
          </button>
        </div>
      </header>

      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} onSearch={searchFromMenu} />
      <SearchOverlay
        open={searchOpen}
        initialQuery={searchQuery}
        onClose={() => {
          setSearchOpen(false);
          setSearchQuery("");
        }}
      />
      <CartDrawer />
    </>
  );
};
